import {useState, useEffect, useMemo} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useIntl, FormattedMessage} from 'react-intl'
import {register, sendVerificationCode} from '../core/_requests'
import {Link, useNavigate, useLocation} from 'react-router-dom'
import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import Swal from 'sweetalert2'

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  changepassword: '',
  code: '',
  referralCode: '',
  acceptTerms: false,
}

export function Registration() {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(1)
  const [verificationCode, setVerificationCode] = useState('')
  const [resendTimer, setResendTimer] = useState(120)
  const navigate = useNavigate()
  const location = useLocation()

  const registrationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstname: Yup.string()
          .min(3, intl.formatMessage({id: 'AUTH.VALIDATION.MIN_LENGTH'}, {name: intl.formatMessage({id: 'AUTH.INPUT.FIRSTNAME'}), min: 3}))
          .max(50, intl.formatMessage({id: 'AUTH.VALIDATION.MAX_LENGTH_FIELD'}) + ' 50')
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.REQUIRED'}, {name: intl.formatMessage({id: 'AUTH.INPUT.FIRSTNAME'})})),
        email: Yup.string()
          .email(intl.formatMessage({id: 'AUTH.VALIDATION.INVALID'}, {name: intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}))
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.REQUIRED'}, {name: intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})})),
        lastname: Yup.string()
          .min(3, intl.formatMessage({id: 'AUTH.VALIDATION.MIN_LENGTH'}, {name: intl.formatMessage({id: 'AUTH.INPUT.LASTNAME'}), min: 3}))
          .max(50, intl.formatMessage({id: 'AUTH.VALIDATION.MAX_LENGTH_FIELD'}) + ' 50')
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.REQUIRED'}, {name: intl.formatMessage({id: 'AUTH.INPUT.LASTNAME'})})),
        password: Yup.string()
          .min(8, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MIN'}))
          .max(50, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MAX'}))
          .matches(
            /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/,
            intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_PATTERN'})
          )
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_REQUIRED'})),
        changepassword: Yup.string()
          .min(8, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MIN'}))
          .max(50, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MAX'}))
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.REQUIRED'}, {name: intl.formatMessage({id: 'AUTH.INPUT.CONFIRM_PASSWORD'})}))
          .oneOf([Yup.ref('password')], intl.formatMessage({id: 'AUTH.VALIDATION.CONFIRM_PASSWORD_MISMATCH'})),
        acceptTerms: Yup.bool().required(intl.formatMessage({id: 'AUTH.VALIDATION.AGREEMENT_REQUIRED'})),
      }),
    [intl]
  )

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      if (step === 1) {
        try {
          const randomCode = Math.floor(1000 + Math.random() * 9000).toString()
          const response = await sendVerificationCode(values.email, randomCode)
          if (response?.data?.status) {
            setVerificationCode(randomCode)
            setStep(2)
            Swal.fire(intl.formatMessage({id: 'AUTH.LOGIN.SUCCESS_TITLE'}), intl.formatMessage({id: 'AUTH.REGISTER.CODE_SENT_SUCCESS'}), 'success')
          } else {
            Swal.fire(intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}), response?.data?.error || intl.formatMessage({id: 'AUTH.REGISTER.CODE_SEND_ERROR'}), 'error')
          }
        } catch (error: any) {
          Swal.fire(
            intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}),
            error?.response?.data?.message || intl.formatMessage({id: 'AUTH.REGISTER.CODE_SEND_ERROR'}),
            'error'
          )
        } finally {
          setLoading(false)
        }
      } else {
        if (values.code === verificationCode) {
          try {
            const {data: auth} = await register(
              values.email,
              values.firstname,
              values.lastname,
              values.password,
              values.changepassword,
              values.referralCode
            )
            if (auth.status) {
              Swal.fire({
                title: intl.formatMessage({id: 'AUTH.LOGIN.SUCCESS_TITLE'}),
                text: intl.formatMessage({id: 'AUTH.REGISTER.SUCCESS'}),
                icon: 'success',
                timer: 2000,
                confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.GO_BUTTON'}),
              })
              navigate('/auth/login', {replace: true})
            } else {
              if (auth.error == 1) {
                Swal.fire({
                  title: intl.formatMessage({id: 'AUTH.REGISTER.ERROR_INCOMPLETE'}),
                  text: intl.formatMessage({id: 'AUTH.REGISTER.ERROR_INCOMPLETE_TEXT'}),
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              } else if (auth.error == 3) {
                Swal.fire({
                  title: intl.formatMessage({id: 'AUTH.REGISTER.ERROR_PASSWORD_INVALID'}),
                  text: intl.formatMessage({id: 'AUTH.REGISTER.ERROR_PASSWORD_INVALID_TEXT'}),
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              } else if (auth.error == 4) {
                Swal.fire({
                  title: intl.formatMessage({id: 'AUTH.REGISTER.ERROR_PASSWORD_MISMATCH'}),
                  text: intl.formatMessage({id: 'AUTH.REGISTER.ERROR_PASSWORD_MISMATCH_TEXT'}),
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              } else if (auth.error == 6) {
                Swal.fire({
                  title: intl.formatMessage({id: 'AUTH.REGISTER.ERROR_EMAIL_EXISTS'}),
                  text: intl.formatMessage({id: 'AUTH.REGISTER.ERROR_EMAIL_EXISTS_TEXT'}),
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              } else {
                Swal.fire({
                  title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}),
                  text: String(auth.error),
                  icon: 'error',
                  confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
                })
              }
            }
          } catch (error) {
            Swal.fire(intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}), intl.formatMessage({id: 'AUTH.REGISTER.ERROR_GENERIC'}), 'error')
          }
        } else {
          Swal.fire(intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}), intl.formatMessage({id: 'AUTH.REGISTER.ERROR_CODE_INVALID'}), 'error')
        }
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const refCode = queryParams.get('ref')
    if (refCode) {
      formik.setFieldValue('referralCode', refCode)
    }

    let timer: NodeJS.Timeout
    PasswordMeterComponent.bootstrap()
    if (step === 2 && resendTimer > 0) {
      timer = setInterval(() => setResendTimer((prev) => prev - 1), 1000)
    }
    return () => clearInterval(timer)
  }, [step, resendTimer, location.search])

  const handleResendCode = async () => {
    const randomCode = Math.floor(1000 + Math.random() * 9000).toString()
    try {
      const response = await sendVerificationCode(formik.values.email, randomCode)
      if (response?.data?.status) {
        setVerificationCode(randomCode)
        setResendTimer(120)
        Swal.fire(intl.formatMessage({id: 'AUTH.LOGIN.SUCCESS_TITLE'}), intl.formatMessage({id: 'AUTH.REGISTER.CODE_SENT_SUCCESS'}), 'success')
      } else {
        Swal.fire(intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}), response?.data?.error || intl.formatMessage({id: 'AUTH.REGISTER.CODE_SEND_ERROR'}), 'error')
      }
    } catch (error: any) {
      Swal.fire(
        intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}),
        error?.response?.data?.message || intl.formatMessage({id: 'AUTH.REGISTER.CODE_SEND_ERROR'}),
        'error'
      )
    }
  }

  return (
    <div className='form-wrapper'>
      <form
        className='form form-inner p-5 mt-3'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signup_form'
      >
        {step === 1 ? (
          <>
            <div className='text-center mb-11'>
              <h1 className='fw-bolder mb-3'>{intl.formatMessage({id: 'AUTH.REGISTER.TITLE'})}</h1>
            </div>
            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}
            <div className='d-block d-md-flex inputs-holder'>
              <div className='fv-row mb-4 input-holder'>
                <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.FIRSTNAME'})}</label>
                <input
                  placeholder={intl.formatMessage({id: 'AUTH.INPUT.FIRSTNAME'})}
                  type='text'
                  {...formik.getFieldProps('firstname')}
                  className={clsx(
                    'form-control bg-transparent custom-input',
                    {'is-invalid': formik.touched.firstname && formik.errors.firstname},
                    {'is-valid': formik.touched.firstname && !formik.errors.firstname}
                  )}
                  autoComplete='off'
                />
                {formik.touched.firstname && formik.errors.firstname && (
                  <div className='fv-plugins-message-container d-none d-md-block'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.firstname}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className='fv-row mb-4 input-holder'>
                <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.LASTNAME'})}</label>
                <input
                  placeholder={intl.formatMessage({id: 'AUTH.INPUT.LASTNAME'})}
                  type='text'
                  {...formik.getFieldProps('lastname')}
                  className={clsx(
                    'form-control bg-transparent custom-input',
                    {'is-invalid': formik.touched.lastname && formik.errors.lastname},
                    {'is-valid': formik.touched.lastname && !formik.errors.lastname}
                  )}
                  autoComplete='off'
                />
                {formik.touched.lastname && formik.errors.lastname && (
                  <div className='fv-plugins-message-container d-none d-md-block'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.lastname}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='fv-row mb-4'>
              <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}</label>
              <input
                placeholder={intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}
                type='email'
                {...formik.getFieldProps('email')}
                className={clsx(
                  'form-control bg-transparent custom-input',
                  {'is-invalid': formik.touched.email && formik.errors.email},
                  {'is-valid': formik.touched.email && !formik.errors.email}
                )}
                autoComplete='off'
              />
              {formik.touched.email && formik.errors.email && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block d-none d-md-block'>
                    <span role='alert'>{formik.errors.email}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='d-block d-md-flex inputs-holder'>
              <div className='fv-row mb-4 input-holder' data-kt-password-meter='true'>
                <div className='mb-1'>
                  <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.PASSWORD'})}</label>
                  <input
                    type='password'
                    placeholder={intl.formatMessage({id: 'AUTH.INPUT.PASSWORD'})}
                    autoComplete='off'
                    {...formik.getFieldProps('password')}
                    className={clsx(
                      'form-control bg-transparent custom-input',
                      {'is-invalid': formik.touched.password && formik.errors.password},
                      {'is-valid': formik.touched.password && !formik.errors.password}
                    )}
                  />
                  {formik.touched.password && formik.errors.password && (
                    <div className='fv-plugins-message-container d-none d-md-block'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.password}</span>
                      </div>
                    </div>
                  )}
                </div>
                <small className='text-muted'>{intl.formatMessage({id: 'AUTH.REGISTER.PASSWORD_HINT'})}</small>
              </div>
              <div className='fv-row mb-5 input-holder'>
                <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.CONFIRM_PASSWORD'})}</label>
                <input
                  type='password'
                  placeholder={intl.formatMessage({id: 'AUTH.INPUT.CONFIRM_PASSWORD'})}
                  autoComplete='off'
                  {...formik.getFieldProps('changepassword')}
                  className={clsx(
                    'form-control bg-transparent custom-input',
                    {'is-invalid': formik.touched.changepassword && formik.errors.changepassword},
                    {'is-valid': formik.touched.changepassword && !formik.errors.changepassword}
                  )}
                />
                {formik.touched.changepassword && formik.errors.changepassword && (
                  <div className='fv-plugins-message-container d-none d-md-block'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.changepassword}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='fv-row'>
              <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.REGISTER.REFERRAL_CODE'})}</label>
              <input
                placeholder={intl.formatMessage({id: 'AUTH.REGISTER.REFERRAL_PLACEHOLDER'})}
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('referralCode')}
                className='form-control bg-transparent custom-input'
              />
            </div>
            <div className='fv-row check-box-holder mt-5'>
              <label className='form-check form-check-inline' htmlFor='kt_login_toc_agree'>
                <input
                  className='form-check-input custom-check-box'
                  type='checkbox'
                  id='kt_login_toc_agree'
                  {...formik.getFieldProps('acceptTerms')}
                />
                <span className='text-rule'>
                  <FormattedMessage
                    id='AUTH.REGISTER.ACCEPT_TERMS'
                    values={{
                      link: (
                        <a href='/terms' target='_blank' rel='noopener noreferrer'>
                          {intl.formatMessage({id: 'AUTH.REGISTER.TERMS_LINK'})}
                        </a>
                      ),
                    }}
                  />
                </span>
              </label>
              {formik.touched.acceptTerms && formik.errors.acceptTerms && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.acceptTerms}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='d-grid mb-10 mt-4'>
              <button
                type='submit'
                id='kt_sign_up_submit'
                className='custom-btn btn-custom-font btn-custom-color rounded-pill btn-lg w-auto'
                disabled={formik.isSubmitting || !formik.isValid || !formik.values.acceptTerms}
              >
                {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'AUTH.GENERAL.SIGNUP_BUTTON'})}</span>}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {intl.formatMessage({id: 'AUTH.LOGIN.PLEASE_WAIT'})}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
            <div className='text-center fs-6'>
              {intl.formatMessage({id: 'AUTH.GENERAL.HAVE_ACCOUNT'})}{' '}
              <Link to='/auth/login' className='link custom-primary-color'>
                {intl.formatMessage({id: 'AUTH.LOGIN.BUTTON'})}
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* فرم تأیید کد */}
            <div className='text-center mb-11'>
              <h1 className='fw-bolder mb-3'>{intl.formatMessage({id: 'AUTH.REGISTER.TITLE'})} – {intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}</h1>
            </div>
            <div className='fv-row mb-3'>
              <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.REGISTER.EMAIL_VERIFY'})}</label>
              <input
                placeholder={intl.formatMessage({id: 'AUTH.REGISTER.CODE_PLACEHOLDER'})}
                autoComplete='off'
                {...formik.getFieldProps('code')}
                className='form-control bg-transparent custom-input'
              />
              {formik.touched.code && formik.errors.code && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.code}</span>
                  </div>
                </div>
              )}
            </div>
            {resendTimer > 0 ? (
              <p>{intl.formatMessage({id: 'AUTH.REGISTER.RESEND_AFTER'}, {seconds: resendTimer})}</p>
            ) : (
              <a role='button' onClick={handleResendCode}>
                {intl.formatMessage({id: 'AUTH.REGISTER.RESEND_CODE'})}
              </a>
            )}
            <div className='d-grid mb-10 mt-4'>
              <button
                type='submit'
                id='kt_sign_up_submit'
                className='custom-btn btn-custom-font btn-custom-color rounded-pill btn-lg w-auto'
                disabled={formik.isSubmitting || !formik.isValid || !formik.values.acceptTerms}
              >
                {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'AUTH.REGISTER.VERIFY_BUTTON'})}</span>}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {intl.formatMessage({id: 'AUTH.LOGIN.PLEASE_WAIT'})}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
            <div className='text-center fs-6'>
              <button type='button' className='btn btn-link p-0' onClick={() => setStep(1)}>
                {intl.formatMessage({id: 'AUTH.REGISTER.EDIT_EMAIL'})}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}
